import { QuotationUnit } from 'data-layer-cr/dist/models';
import { clavePayment, PaymentLegacy } from 'src/constants/paymentTypes';
import {
  LegalBeneficiary,
  Shareholder,
} from 'src/models/helperTypes/EmissionDataFields';
import RequestClaveData from './RequestClaveData';

export interface CreditCardData {
  cardNumber: string; // <- no spaces
  nameOwner: string;
  emailOwner: string;
  phoneNumberOwner: string;
  addressOwner: string;
  expirationMonth: number;
  expirationYear: number;
  securityCode: string;
  cardType: string;

  bankId: string;
  bankDescription: string;
}

export interface ACHData {
  bankId: string;
  accountName: string;
  accountType: string;
  accountNumber: string;
}

export const makeEmptyACHData = (): ACHData => ({
  bankId: '',
  accountName: '',
  accountNumber: '',
  accountType: '',
});

export const makeEmptyCreditCard = (): CreditCardData => ({
  cardNumber: '',
  nameOwner: '',
  emailOwner: '',
  phoneNumberOwner: '',
  addressOwner: '',
  expirationMonth: 0,
  expirationYear: 0,
  securityCode: '',
  cardType: '',
  bankId: '',
  bankDescription: '',
});

interface PaymentBasic {
  amount: number;
}

interface EmissionFirstPayment extends PaymentBasic {
  creditCard: CreditCardData;
  achData: ACHData;
  paymentMethod: PaymentLegacy;
}

interface ClavePayment extends PaymentBasic {
  claveData: RequestClaveData;
  paymentMethod: typeof clavePayment;
}

export type PaymentShape = ClavePayment | EmissionFirstPayment;

export interface EmissionPaymentConditions {
  paymentMethods: string;
  amountPayments: number;
  payDay: number;
  policyStartDate: string;
}

export interface EmissionUnitInformation {
  licensePlate: string;
  color: string;
  serialVin: string;
  serialMotor: string;
  driverIsContractor: boolean;
}

export interface EmissionClientBeneficiary {
  firstName: string;
  lastName: string;
  idType: string;
  id: string;
  participationPercentage: number;
  birthdate: string;
  nationalityId: string;
  nationality: string;
  birthPlaceId: string;
  birthPlace: string;
  occupation: string;
  occupationName: string;
  companyRole: string;
  phone1: string;
  phone2: string;
  addressDetail: string;
  countryId: string;
  country: string;
  stateId: string;
  state: string;
  cityId: string;
  city: string;
  townshipId: string;
  township: string;
}

export interface AddressItem {
  code: string;
  name: string;
}

export interface ClientAddress {
  country: AddressItem;
  state: AddressItem;
  city: AddressItem;
  township: AddressItem;
  detail: string;
}

export interface EmissionClient {
  // We have these
  codCli: string;
  id: string;
  idType: string;
  genderId: string;
  birthdate: string;
  firstName: string;
  lastName: string;
  address: string;
  phone1: string;
  phone2: string;
  phone3: string;
  email: string;

  // We don't have these
  civilStatusId: string;
  pep: boolean;

  // Related to legal entity
  nationalityId: string;
  otherNationalityId?: string;
  economicActivity?: string;
  economicActivityName?: string;
  beneficiary?: EmissionClientBeneficiary;

  // Related to natural person
  profession: string;
  occupation: string;
  occupationName: string;
  enterprise: string;
  enterpriseActivity: string;
  salary: string;
  typeOfMoney: string;
  originCountry: string;
  originCountryName: string;
  originDetail: string;
  originType: string;
  birthPlace: string;
  nationality: string;
  otherNationality?: string;
  civilStatus: string;

  // address
  clientAddress: ClientAddress | null;

  // enterprise data
  socialReason: string;
  notificationPreference: string;

  // shareholders
  shareholders: Shareholder[];

  // enterprise stock exchange data
  isInStockExchange: boolean;
  stockExchangeName: string;
  stockExchangeCountry: string;
  stockExchangeCountryName: string;
  companyType: string;

  // legalBeneficiaries
  legalBeneficiaries: LegalBeneficiary[];
}

export interface EmissionDocument {
  id: string;
  name: string;
  content: string;
  mime: string;
}

export interface FinancingInformation {
  financingType: string;
  creditor: string;
  creditorDescription: string;
  creditAmount: number;
}

export interface IssuerInformation {
  id: string;
  role: string;
  license: string;
  email: string;
  name: string;
}

export default interface EmissionRequest {
  productId: string;
  quotationId: string;
  unitInformation: EmissionUnitInformation;
  firstPayment: PaymentShape;
  paymentConditions: EmissionPaymentConditions;
  client: EmissionClient;
  paymentIssuer: EmissionClient;

  /** Note the typo, it is intentional to match the backend */
  aditionalDocuments: EmissionDocument[];

  financingInformation: FinancingInformation;
  createdBy: string;
  validSince: string;
  emissionRequestId: string;
  packageOption: number;
  additionalEmails: string[];
  issuedBy: IssuerInformation | null;
  assignDateSignature: boolean;

  units: QuotationUnit[];

  acceptInformedConsent?: boolean;
}
